import React, { useCallback, useMemo, useState } from 'react';
import { HbrDomTable, HbrSpinner } from '@harbor/elements-react-standalone';

import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import { useFetchAllDeliveries, useFetchAllWorkshopMeetings } from '../../../hooks/campaign-hooks';
import { useCampaignRollupData } from '../../../hooks/useCampaignRollupData';
import { useCampaignParticipationRollup } from '../../../hooks/useCampaignRollups';
import type { OrgChartRow } from '../../../support/orgScope';
import { useCampaignPipeline } from '../../../hooks/useCampaignPipeline';
import { useSfRecordsById } from '../../../hooks/useSfRecordsById';
import { CacheKey } from '../../../types/cache-keys';
import { getSfRecord } from '../../../support/salesforceBatch';
import {
  deliveriesByOwner,
  deliveryPipeline,
  formatPipelineUsd,
  filterDeliveriesByWindow,
  withScheduledStatus,
  type DateWindow,
} from '../../../support/campaignLeader';
import {
  DELIVERY_STATUSES,
  deliveryStatusLabel,
  type CampaignDelivery,
} from '../../../support/campaigns';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import CampaignsDashboardSections from './CampaignsDashboardSections';
import { CampaignsParticipationIndividual } from './CampaignsParticipationSections';

/**
 * Leader > Campaigns > Individual. One SE's campaign work: their deliveries (bucketed by
 * `owner === email`) summarised in the same Deliveries and Enablement sections as the team page, and a
 * per-delivery detail table. The dashboard and the table are computed from the same campaign-scoped
 * data, so they always describe the same campaign.
 */

interface Props {
  email: string;
  /** Org rows, to attribute enrollments (which carry a user id) to a person. */
  orgRows: OrgChartRow[];
  /** When set, scope this SE's campaign work to one campaign; undefined = across all campaigns. */
  campaignId?: number;
  /** The selected campaign's name, for headings. */
  campaignName?: string;
  /** Inclusive fiscal window from the time dropdown; null/undefined = all time. */
  timeWindow?: DateWindow | null;
  /** The period in words (e.g. "This Fiscal Quarter"), for headings. */
  timeShort?: string;
  /** The fiscal calendar is still loading. */
  timePending?: boolean;
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function CampaignsIndividualContributorPage({
  email,
  orgRows,
  campaignId,
  campaignName,
  timeWindow = null,
  timeShort,
  timePending = false,
}: Props) {
  const [isListOpen, setListOpen] = useState(true);

  const owner = String(email || '').toLowerCase();
  const { data: rawDeliveries, isLoading: deliveriesLoading, error } = useFetchAllDeliveries();
  // "Scheduled" is derived from booked workshop meetings (nothing stores it on the delivery).
  const { data: meetings, isLoading: meetingsLoading } = useFetchAllWorkshopMeetings();
  const isLoading = deliveriesLoading || meetingsLoading;
  const { targetsByDelivery, amountByOpp } = useCampaignPipeline();

  // Scope to the selected campaign (leaders view campaigns individually) before any owner/user bucketing.
  const deliveries = useMemo(() => {
    const forCampaign =
      campaignId == null
        ? rawDeliveries ?? []
        : (rawDeliveries ?? []).filter((d) => d.campaign === campaignId);
    return filterDeliveriesByWindow(withScheduledStatus(forCampaign, meetings), timeWindow);
  }, [rawDeliveries, meetings, campaignId, timeWindow]);

  const mine = useMemo(() => {
    const byOwner = deliveriesByOwner(deliveries ?? []);
    return (byOwner.get(owner) ?? []).slice().sort((a, b) => {
      // Most recently scheduled first; undated fall to the end.
      const ta = a.scheduled_for ? Date.parse(a.scheduled_for) : 0;
      const tb = b.scheduled_for ? Date.parse(b.scheduled_for) : 0;
      return tb - ta;
    });
  }, [deliveries, owner]);

  // The customer account(s) a delivery is for hang off its targets; the account NAME is read from Salesforce
  // here (never stored on the delivery), falling back to a legacy `customer_name` label.
  const accountIds = useMemo(() => {
    const ids = new Set<string>();
    for (const d of mine) for (const t of targetsByDelivery.get(d.id) ?? []) if (t.customer) ids.add(t.customer);
    return Array.from(ids);
  }, [mine, targetsByDelivery]);
  const { byId: accountById, loading: accountsLoading } = useSfRecordsById<{ Name?: string | null }>(
    'Account',
    ['Name'],
    accountIds,
    { cacheKey: String(CacheKey.SalesforceCustomer) },
  );

  // The dashboard figures come from the same rollup source as the team page (server rollups, with a
  // browser-side fallback); the list below is this person's individual deliveries.
  const data = useCampaignRollupData({
    manager: owner,
    orgRows,
    campaignId,
    window: timeWindow,
    enabled: !timePending,
  });
  const participationQ = useCampaignParticipationRollup({
    manager: owner,
    campaignId,
    window: timeWindow,
    enabled: !timePending,
  });
  const campaignScope = campaignId != null ? campaignName || `Campaign ${campaignId}` : 'All campaigns';
  const scopeLabel = timeWindow && timeShort ? `${campaignScope} · ${timeShort}` : campaignScope;

  const visibleColumns: Column[] = useMemo(() => {
    const col = (id: string, name: string, width: number): Column => ({
      id,
      name,
      width,
      columnType: ColumnType.Custom,
      sortable: false,
      align: ColumnAlignment.Left,
      dataType: DataType.String,
      resizable: false,
      reorderable: false,
    });
    return [
      col('campaign', 'Campaign', 260),
      col('delivery', 'Delivery', 200),
      col('account', 'Customer account', 240),
      col('status', 'Status', 140),
      col('pipeline', 'Pipeline $', 130),
      col('scheduled', 'Scheduled', 130),
      col('completed', 'Completed', 130),
    ]
      .map((c) => getColumnTypeConfig(c))
      .map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  const accountLabel = useCallback(
    (d: CampaignDelivery): string => {
      const names: string[] = [];
      for (const t of targetsByDelivery.get(d.id) ?? []) {
        const name = (t.customer ? getSfRecord(accountById, t.customer)?.Name : null) || t.customer_name;
        if (name && !names.includes(name)) names.push(name);
        else if (!name && t.customer && accountsLoading) return 'Loading…';
      }
      return names.length ? names.join(', ') : '—';
    },
    [targetsByDelivery, accountById, accountsLoading],
  );

  const rows = useMemo(
    () =>
      mine.map((d: CampaignDelivery) => ({
        id: String(d.id),
        campaign: d.campaign_name || `Campaign ${d.campaign}`,
        delivery: d.name || '—',
        account: accountLabel(d),
        status: deliveryStatusLabel(d.status),
        pipeline: formatPipelineUsd(deliveryPipeline(d, targetsByDelivery, amountByOpp)),
        scheduled: fmtDate(d.scheduled_for),
        completed: fmtDate(d.completed_at),
      })),
    [mine, targetsByDelivery, amountByOpp, accountLabel],
  );

  if (error) {
    return <div style={{ padding: 12, color: 'red' }}>{(error as Error).message}</div>;
  }

  const renderHeader = () =>
    visibleColumns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div className="w-max">{column.name}</div>
      </div>
    ));

  const renderBody = () => {
    if (!rows.length) return null;
    const list: JSX.Element[] = [];
    for (const row of rows) {
      for (const column of visibleColumns) {
        const slot = `row-${row.id}-column-${column.id}`;
        list.push(
          <div key={slot} slot={slot} className="min-h-8 flex items-center box-content">
            {(row as any)[column.id]}
          </div>,
        );
      }
    }
    return list;
  };

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      {data.deliveries && data.pipeline ? (
        <CampaignsDashboardSections
          deliveries={data.deliveries}
          pipeline={data.pipeline}
          enrollment={data.enrollment}
          enrollmentLoading={data.enrollmentLoading}
          scopeLabel={scopeLabel}
          keyPrefix={`camp-ic-${owner}-${campaignId ?? 'all'}-${timeWindow?.start ?? 'all'}`}
          enablementNote={timeWindow ? 'Enrollment progress is not filtered by the time period.' : undefined}
        />
      ) : (
        <HbrSpinner>Loading campaigns…</HbrSpinner>
      )}

      <CampaignsParticipationIndividual
        email={owner}
        orgRows={orgRows}
        participation={{
          data: participationQ.data,
          loading: timePending || participationQ.isLoading,
          error: (participationQ.error as Error | null) ?? null,
        }}
        scopeLabel={scopeLabel}
        periodActive={!!timeWindow}
      />

      <StbAccordion variant="minimal" label={`Deliveries list — ${scopeLabel}`} isOpen={isListOpen} toggleIsOpen={setListOpen}>
        <div style={{ minHeight: 200, padding: 12 }}>
          {isLoading ? (
            <HbrSpinner>Loading deliveries…</HbrSpinner>
          ) : rows.length === 0 ? (
            <div className="hbr-css__text-secondary-sm">No campaign deliveries owned by {owner}.</div>
          ) : (
            <HbrDomTable autoLayout={true} columns={visibleColumns} data={rows}>
              {renderHeader()}
              {renderBody()}
            </HbrDomTable>
          )}
        </div>
      </StbAccordion>
    </div>
  );
}
